import { useFragment } from '@apollo/client';
import { FeatureFlag } from '@ps/web-core-enums';
import type { EventId } from '@ps/web-sports-api-types';
import React from 'react';

import useFeature from '@swf/core/hooks/useFeature';
import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import type { EventTypeId } from '@swf/sports/types/eventType';
import { getEventScoreboardFragment } from '@swf/sports/utils/getEventScoreboardFragment';
import { getIsScoreboardSupportedMarketLevel } from '@swf/sports/utils/getIsScoreboardSupportedMarketLevel';
import type { LiveData } from '@swf/sports/utils/getLiveData';

import type { Props as DefaultEventStatusProps } from './components/DefaultEventStatus/DefaultEventStatus';
import DefaultEventStatus from './components/DefaultEventStatus/DefaultEventStatus';
import ScoreboardInPlayStatus from './components/InPlayStatus/ScoreboardInPlayStatus';

export interface Props extends DefaultEventStatusProps {
  eventId?: EventId;
  eventTypeId: EventTypeId;
}

const EventStatus: React.FC<Props> = (props) => {
  const { isLoading, isError, variables } = useLiveData();
  const { data, complete, missing } = useFragment<LiveData>(
    getEventScoreboardFragment(props.eventTypeId, props.eventId ?? Infinity, variables)
  );
  const isScoreboardsEnabled = useFeature(FeatureFlag.ISPEnableLiveData);

  const isScoreboardSupportedMarketLevel = getIsScoreboardSupportedMarketLevel(props.marketLevels);
  const isMissingData = missing || !complete;

  if (
    isScoreboardsEnabled &&
    !isLoading &&
    !isError &&
    !isMissingData &&
    isScoreboardSupportedMarketLevel
  ) {
    return <ScoreboardInPlayStatus liveData={data} eventTypeId={props.eventTypeId} />;
  }

  return (
    <DefaultEventStatus
      {...props}
      timeFormat={props.timeFormat || 'time'}
      size={props.size || 'XSmall'}
    />
  );
};

export default EventStatus;
